// Push приложения на телефоне — FCM (решение владельца 2026-09-26: предупреждения
// заранее о сроках «в Telegram, push приложения или оба — на выбор»).
//
// Только Android-сборка с плагином: планшету Windows и браузеру push не приходит.
// У Windows свой канал — лента точки и системные уведомления (T-047, `modules/notifications/feed`).
// Старый APK (до 0.2.31) плагина не несёт — веб-слой приезжает раньше нативного,
// поэтому доступность проверяется у самой оболочки, а не по версии.
//
// Push принадлежит ЧЕЛОВЕКУ (решение владельца 2026-09-29, B-039: «проработай так, чтобы
// не слетала»). Раньше галочку рисовало наличие адреса на телефоне, а адрес жил временем
// человека — и любое «Выйти» снимало push, а вход его не возвращал. Теперь:
// - выбор «присылать push» и пороги хранит сервер (`alert-settings`), телефон — нет;
// - адрес FCM принадлежит УСТАНОВКЕ (ключ `device`). При каждом входе и запуске телефон
//   привязывает его к сессии вошедшего (`sync`) — всегда, при любом выборе: доставку
//   решает сервер по выбору человека, и push, включённый с любого устройства, сразу
//   доходит до всех телефонов, где человек вошёл;
// - доставку на телефон прекращает сервер, когда гаснет сессия: выход, отзыв, idle-TTL.
//   Снимать адрес при выходе клиенту не нужно — и «Выйти» не ждёт сеть ради push.
//
// Прежний сервер (404 на alert-settings — откат сервера после выката бандла) знает только
// «строка телефона = согласие». Для него остаётся одно: при выходе, если сервер точно
// прежний, снять свою строку (`forgetPushOnLogout`).

import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  PushNotifications,
  type PermissionStatus,
  type PushNotificationsPlugin,
} from '@capacitor/push-notifications';

import { httpClient, HttpError, isUndelivered } from '@/shared/api';
import type { components } from '@/shared/api/generated/schema';
import { getToken, isSupportSession } from '@/shared/auth';
import { reportError } from '@/shared/lib';
import { PREFERENCE_KEYS } from '@/shared/storage';

import { currentPushHere, setPushHere, type PushHere } from './pushHere';

type PushDeviceSyncRequest = components['schemas']['PushDeviceSync'];
type PushDeviceSyncResponse = components['schemas']['PushDeviceSyncResult'];

const FORGET = '/tenants/me/push-devices/forget/';
const SYNC = '/tenants/me/push-devices/sync/';
const ALERT_SETTINGS = '/tenants/me/alert-settings/';

/** Канал уведомлений Android — тот же, что шлёт сервер (`push.CHANNEL_ID`). */
export const PUSH_CHANNEL = 'expiry';

/** Куда ведёт нажатие, если сервер маршрут не прислал: «В работе» открыт любой роли. */
export const DEFAULT_PUSH_ROUTE = '/kiosk/labels';

/** Сколько ждать адрес от Google: без сети регистрация не отвечает вовсе. */
const REGISTRATION_TIMEOUT_MS = 20_000;

/**
 * Потолок ожидания `sync`. У fetch таймаута нет, и на зависшей сети запрос висел бы
 * до обрыва соединения: подключения стоят в очереди друг за другом, и одно зависшее
 * держало бы и кнопку окна, и смену адреса Google (ревью B-039).
 */
const SYNC_TIMEOUT_MS = 20_000;

/** Для тестов — реальный код берёт зарегистрированный плагин. */
export type PushDeps = {
  readonly plugin?: PushNotificationsPlugin;
  readonly platform?: string;
  readonly pluginAvailable?: boolean;
};

function pluginOf(deps: PushDeps): PushNotificationsPlugin {
  return deps.plugin ?? PushNotifications;
}

/** Умеет ли эта установка push: Android-сборка, в которой есть плагин. */
export function pushSupported(deps: PushDeps = {}): boolean {
  const platform = deps.platform ?? Capacitor.getPlatform();
  const available = deps.pluginAvailable ?? Capacitor.isPluginAvailable('PushNotifications');
  return platform === 'android' && available;
}

// --- Адрес установки ------------------------------------------------------------------

/** Прежний ключ перенесён в этом процессе: второй раз его не читаем. */
let legacyMigrated = false;

/**
 * Адрес этой установки в FCM.
 *
 * Прежний ключ (`restomarker.session.pushToken`, время жизни человека) переносится
 * один раз и удаляется: новый бандл приезжает по воздуху поверх старого, и адрес,
 * записанный старым, нужен как `previous_token`, иначе его строка осталась бы на сервере
 * дублем. Оставлять старый ключ нельзя: откаченный старый бандл прочитал бы его как «push
 * включён» и записал бы адрес на того, кто сейчас вошёл, — без его согласия.
 */
async function readPushAddress(): Promise<string | null> {
  if (!legacyMigrated) {
    const { value: legacy } = await Preferences.get({ key: PREFERENCE_KEYS.legacyPushToken });
    if (legacy) {
      await Preferences.set({ key: PREFERENCE_KEYS.pushAddress, value: legacy });
      await Preferences.remove({ key: PREFERENCE_KEYS.legacyPushToken });
    }
    legacyMigrated = true;
  }
  const { value } = await Preferences.get({ key: PREFERENCE_KEYS.pushAddress });
  return value || null;
}

async function savePushAddress(token: string, stored: string | null): Promise<void> {
  if (token !== stored) await Preferences.set({ key: PREFERENCE_KEYS.pushAddress, value: token });
}

/** Адрес этого телефона в FCM: `register()` отдаёт его событием, а не результатом. */
function obtainToken(plugin: PushNotificationsPlugin): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const handles: Array<Promise<PluginListenerHandle>> = [];
    let settled = false;
    const finish = (settle: () => void): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      for (const handle of handles) void handle.then((h) => h.remove());
      settle();
    };
    const timer = setTimeout(
      () => finish(() => reject(new Error('Google не выдал адрес для уведомлений'))),
      REGISTRATION_TIMEOUT_MS,
    );
    handles.push(
      plugin.addListener('registration', (token) => finish(() => resolve(token.value))),
      plugin.addListener('registrationError', (error) =>
        finish(() => reject(new Error(error.error))),
      ),
    );
    plugin.register().catch((error: unknown) => finish(() => reject(error)));
  });
}

/**
 * Канал с высокой важностью — иначе Android кладёт предупреждение о сроке в шторку
 * молча. Заводится повторно без вреда: существующий канал система не пересоздаёт.
 */
async function ensureChannel(plugin: PushNotificationsPlugin): Promise<void> {
  await plugin
    .createChannel({
      id: PUSH_CHANNEL,
      name: 'Сроки годности',
      description: 'Предупреждения заранее, когда у заготовки подходит срок',
      importance: 5,
      visibility: 1,
      vibration: true,
    })
    .catch(() => undefined);
}

/** Спросить ещё можно: Android покажет системный запрос (оба вида «prompt»). */
function canAsk(permission: PermissionStatus): boolean {
  return permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale';
}

const OFF: PushHere = { status: 'off', canAsk: false };

/**
 * Что показать под галочкой при включённом выборе: одно правило на подключение, смену
 * адреса и перечитывание разрешения.
 */
function hereWith(permission: PermissionStatus): PushHere {
  if (permission.receive === 'granted') return { status: 'connected', canAsk: false };
  return { status: 'blocked', canAsk: canAsk(permission) };
}

/**
 * Привязать адрес к сессии вошедшего. `registered` — включён ли у него push: доставку
 * решает сервер, адрес привязывается при любом выборе (B-039).
 */
async function syncAddress(token: string, previous: string | null): Promise<boolean> {
  const body: PushDeviceSyncRequest = {
    token,
    ...(previous !== null && previous !== token ? { previous_token: previous } : {}),
    platform: 'android',
    app_version: __APP_VERSION__,
  };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SYNC_TIMEOUT_MS);
  try {
    const { registered } = await httpClient.post<PushDeviceSyncResponse>(SYNC, body, {
      signal: controller.signal,
    });
    return registered;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Сбой, который повтором не лечится (4xx, отказ плагина), — видимое «не подключён»
 * и след в мониторинге, а не молчание: «галочка стоит, а push не приходит» без строки
 * под ней неотличимо от «слетело» (ревью B-039, code-quality.md §6).
 *
 * Кроме отказа сессии (401/403): её погасили, и разберёт это `AppLayout`, а для
 * мониторинга это не сбой push.
 */
function failVisibly(error: unknown): void {
  if (error instanceof HttpError && (error.status === 401 || error.status === 403)) return;
  reportError(error);
  setPushHere({ status: 'failed', canAsk: false });
}

// --- Подключение этого телефона (B-039) -------------------------------------------------

/** Кому сообщить, что `sync` не дошёл и его стоит повторить (хук `usePushSync`). */
let onUndelivered: (() => void) | null = null;

/** Хук синхронизации подписывается на недошедшие `sync` — в том числе от смены адреса. */
export function setPushRetryHandler(handler: (() => void) | null): void {
  onUndelivered = handler;
}

/**
 * Очередь работы с адресом: подключения и смены адреса Google идут по одному. Вход и
 * кнопка окна могут совпасть, и второе, прочитав адрес до записи первого, прислало бы
 * уже снятый `previous_token`. Адрес, который выдаст `register()` самого подключения,
 * приходит и событием смены адреса — в очереди оно застаёт его уже сохранённым
 * и второй раз его не отправляет.
 */
let queue: Promise<unknown> = Promise.resolve();

function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const run = queue.catch(() => undefined).then(task);
  queue = run;
  return run;
}

/**
 * Адрес от Google, ещё не дошедший до сервера: сменился, пока сессия не подключалась,
 * или его `sync` не дошёл. Его отправит следующее подключение — и при выключенном
 * выборе, когда FCM не будится. Только в памяти: после перезапуска свежий адрес возьмёт
 * подключение при включённом выборе.
 */
let unsentAddress: string | null = null;

export type ConnectOptions = {
  /**
   * Показать системный запрос разрешения, если его ещё можно задать. Только по нажатию
   * человека или при первом подключении личного телефона после входа — никогда при
   * повторе и никогда само на закреплённом планшете (ревью B-039).
   */
  readonly ask: boolean;
  /** Выбор человека выключен: привязать сохранённый адрес, FCM не будить. */
  readonly quiet?: boolean;
};

/**
 * Подключить этот телефон к push вошедшего человека: разрешение, канал, свежий адрес
 * от Google, `sync`. С `quiet` — только `sync` уже известного адреса.
 *
 * Адрес регистрируется и при запрещённых уведомлениях: Android их просто не покажет,
 * а как только человек разрешит их в настройках, push заработает без перезахода.
 *
 * Результат — ответ сервера: включён ли push у вошедшего (`null` — на сервер ничего
 * не ушло). Он может разойтись с выбором, по которому звали: выбор успели сменить
 * с другого телефона.
 *
 * Отклоняется только недошедшим `sync` (`isUndelivered`) — повтор забота вызывающего.
 * Остальные сбои видны строкой «не подключён» и уходят в мониторинг.
 */
export function connectThisPhone(
  options: ConnectOptions,
  deps: PushDeps = {},
): Promise<boolean | null> {
  const plugin = pluginOf(deps);
  return enqueue(() => connectOnce(options, plugin));
}

async function connectOnce(
  options: ConnectOptions,
  plugin: PushNotificationsPlugin,
): Promise<boolean | null> {
  try {
    const stored = await readPushAddress();
    if (options.quiet) {
      // Выбор выключен: FCM не будится. Адреса нет — привязывать нечего.
      const address = unsentAddress ?? stored;
      if (address === null) {
        setPushHere(OFF);
        return null;
      }
      const registered = await syncAddress(address, stored);
      await savePushAddress(address, stored);
      if (unsentAddress === address) unsentAddress = null;
      // Разрешение важно, только если выбор успели включить: иначе строки под галочкой нет.
      setPushHere(registered ? hereWith(await plugin.checkPermissions()) : OFF);
      return registered;
    }

    let permission = await plugin.checkPermissions();
    if (options.ask && canAsk(permission)) {
      const asked = permission;
      permission = await plugin.requestPermissions().catch(() => asked);
    }
    await ensureChannel(plugin);

    // Google не ответил за 20 с (нет сети, Play Services после обновления APK) —
    // подключается известный адрес: скорее всего он тот же, и лучше он, чем никакого.
    const token = await obtainToken(plugin).catch(() => unsentAddress ?? stored);
    if (token === null) {
      setPushHere({ status: 'failed', canAsk: false });
      return null;
    }

    const registered = await syncAddress(token, stored);
    // После ответа сервера, а не до: пока `sync` не дошёл, прежний адрес ещё нужен
    // как `previous_token` для повтора.
    await savePushAddress(token, stored);
    unsentAddress = null;
    setPushHere(registered ? hereWith(permission) : OFF);
    return registered;
  } catch (error) {
    if (isUndelivered(error)) throw error;
    failVisibly(error);
    return null;
  }
}

/**
 * Google сменил адрес, пока приложение открыто: привязать новый, снять прежний — при
 * любом выборе, как при входе.
 *
 * Только при живой СВОЕЙ сессии, которую в этом запуске уже подключали; иначе адрес
 * ждёт её подключения (`unsentAddress`). Недошедший `sync` — повтор хуком; прежний адрес
 * сохраняется до ответа сервера и уедет в `previous_token`.
 */
async function applyRotatedAddress(token: string, plugin: PushNotificationsPlugin): Promise<void> {
  if (currentPushHere() === null || getToken() === null || isSupportSession()) {
    unsentAddress = token;
    return;
  }
  try {
    const stored = await readPushAddress();
    if (stored === token) return;
    const registered = await syncAddress(token, stored);
    await savePushAddress(token, stored);
    if (unsentAddress === token) unsentAddress = null;
    setPushHere(registered ? hereWith(await plugin.checkPermissions()) : OFF);
  } catch (error) {
    if (isUndelivered(error)) {
      unsentAddress = token;
      onUndelivered?.();
    } else {
      failVisibly(error);
    }
  }
}

/**
 * Перечитать разрешение Android — окно открыли или вернулись в приложение из настроек.
 *
 * Android не перезапускает приложение, когда человек разрешает уведомления в настройках,
 * и без перечитывания строка продолжала бы говорить «разрешите их» после того, как он
 * разрешил (ревью B-039). Без запроса разрешения: только чтение.
 */
export async function refreshPushPermission(deps: PushDeps = {}): Promise<void> {
  const here = currentPushHere();
  if (!pushSupported(deps) || here === null || here.status === 'off' || here.status === 'failed') {
    return;
  }
  const permission = await pluginOf(deps)
    .checkPermissions()
    .catch(() => null);
  if (permission === null) return;
  const next = hereWith(permission);
  // Пока читали, телефон могли подключить заново — его итог новее.
  if (currentPushHere() !== here) return;
  if (next.status !== here.status || next.canAsk !== here.canAsk) setPushHere(next);
}

// --- Прежний сервер (404 на alert-settings) ---------------------------------------------

let legacyServer = false;

/**
 * Сервер ответил на alert-settings: 404 — прежний (до T-046), иначе новый. Сбрасывается
 * любым успешным ответом: сервер обновили, пока приложение было открыто.
 */
export function rememberLegacyPushServer(legacy: boolean): void {
  legacyServer = legacy;
}

export function isLegacyPushServer(): boolean {
  return legacyServer;
}

/**
 * Сервер ТОЧНО прежний прямо сейчас — свежим запросом, а не по флагу.
 *
 * Флаг мог устареть: сервер обновили, пока приложение было открыто, — и прежний
 * `forget/` на новом сервере выключил бы push человека на всех телефонах, то есть вернул
 * бы B-039 (ревью). Не ответил — «не знаю»: `forget/` не шлём, новому серверу он вреден.
 */
export async function confirmLegacyPushServer(signal?: AbortSignal): Promise<boolean> {
  try {
    await httpClient.get(ALERT_SETTINGS, { signal });
    rememberLegacyPushServer(false);
    return false;
  } catch (error) {
    const legacy = error instanceof HttpError && error.status === 404;
    if (legacy) rememberLegacyPushServer(true);
    return legacy;
  }
}

/**
 * Прежний сервер, выход: снять свою строку телефона, пока сессия ещё жива. У такого
 * сервера строка и есть согласие, а доставка сессию не смотрит: без снятия следующему,
 * кто войдёт на этом телефоне, приходили бы предупреждения ушедшего.
 *
 * Не дошло (сеть, потолок выхода) — адрес FCM установки гасится (`unregister()`): только
 * так телефон гарантированно перестаёт получать push по строке, оставшейся на сервере.
 * На новом сервере этого не делается никогда — там адрес нужен установке и дальше.
 */
export async function forgetPushOnLogout(
  options: { readonly signal?: AbortSignal } = {},
  deps: PushDeps = {},
): Promise<void> {
  unsentAddress = null;
  const token = await readPushAddress();
  if (token === null) return;
  await Preferences.remove({ key: PREFERENCE_KEYS.pushAddress });
  try {
    await httpClient.post(FORGET, { token }, { signal: options.signal });
  } catch {
    if (pushSupported(deps)) {
      await pluginOf(deps)
        .unregister()
        .catch(() => undefined);
    }
  }
}

// --- Уведомления ----------------------------------------------------------------------

export type PushHandlers = {
  /** Человек нажал на уведомление — открыть экран. */
  readonly onOpen: (route: string) => void;
  /** Уведомление пришло, пока приложение на экране: Android сам его не покажет. */
  readonly onForeground: (title: string, body: string) => void;
};

/**
 * Подписаться на нажатия, на уведомления при открытом приложении и на смену адреса
 * Google — один раз на старте.
 */
export function installPushHandlers(handlers: PushHandlers, deps: PushDeps = {}): void {
  if (!pushSupported(deps)) return;
  const plugin = pluginOf(deps);
  void plugin.addListener('pushNotificationActionPerformed', (action) => {
    const route: unknown = action.notification.data?.route;
    handlers.onOpen(
      typeof route === 'string' && route.startsWith('/') ? route : DEFAULT_PUSH_ROUTE,
    );
  });
  void plugin.addListener('pushNotificationReceived', (notification) => {
    handlers.onForeground(notification.title ?? '', notification.body ?? '');
  });
  void plugin.addListener('registration', (token) => {
    void enqueue(() => applyRotatedAddress(token.value, plugin));
  });
}

/** Только для тестов: состояние модуля между тестами не переживает. */
export function __resetPushForTests(): void {
  legacyMigrated = false;
  queue = Promise.resolve();
  unsentAddress = null;
  onUndelivered = null;
  legacyServer = false;
}
